/**
 * FundRaisingAgentsPanel
 * Per-agent status cards with last run info and manual trigger.
 * Agents: Scout | Analyst | Builder | Compliance | Coordinator
 *
 * Top-level controls:
 *  - "Run Full Pipeline" — scout (seed) → analyst, all channels, sequential
 *  - "Seed Overviews" on Scout card — scout seed mode only
 *  - "Run scout" on Scout card — monitor/diff mode only
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/opo/fundraising';

const AGENT_INFO = {
  scout:       { icon: '🔍', cadence: 'Weekly — Mon 06:00 PT', purpose: 'Fetches official program pages; detects status/intake changes; seeds OVERVIEW.md files' },
  analyst:     { icon: '📊', cadence: 'Nightly — 02:00 PT',    purpose: 'Scores every channel fit 0–100 against the OptiPrime profile; writes rationale' },
  builder:     { icon: '✍️',  cadence: 'On trigger',            purpose: 'Drafts 6 application sections using knowledge base + company profile' },
  compliance:  { icon: '✅', cadence: 'On draft change',        purpose: 'Validates draft against requirements.json; blocks draft→review until score ≥ threshold' },
  coordinator: { icon: '🎯', cadence: 'Always on',             purpose: 'Department orchestrator — summarises state, surfaces top-fit programs, logs cycle' },
};

function fmtDate(s) {
  return s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}
function fmtDuration(start, end) {
  if (!start) return '—';
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
}

// Pill showing run mode
function ModePill({ mode }) {
  if (!mode) return null;
  const colors = {
    'pipeline-seed': { bg: 'rgba(99,102,241,.2)', color: '#818cf8' },
    'pipeline-score': { bg: 'rgba(99,102,241,.2)', color: '#818cf8' },
    'manual-seed': { bg: 'rgba(16,185,129,.15)', color: '#10b981' },
    'manual':  { bg: 'rgba(59,130,246,.15)', color: '#3b82f6' },
    'scheduled': { bg: 'rgba(107,114,128,.15)', color: '#9ca3af' },
  };
  const c = colors[mode] || colors['manual'];
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 8, background: c.bg, color: c.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {mode.replace(/-/g, ' ')}
    </span>
  );
}

export default function FundRaisingAgentsPanel() {
  const [agentsData, setAgentsData] = useState(null);
  const [running, setRunning] = useState({});
  const [runMsg, setRunMsg] = useState({});
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState({});

  // Pipeline state
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineMsg, setPipelineMsg] = useState('');
  const [pipelinePhase, setPipelinePhase] = useState('');  // 'scout' | 'analyst' | ''

  const load = useCallback(() => {
    fetch(`${API}/agents`).then(r => r.json()).then(setAgentsData).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    fetch(`${API}/channels`).then(r => r.json()).then(d => setChannels(d.channels || [])).catch(() => {});
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const handleRun = async (agentType, extraBody = {}) => {
    const key = agentType + (extraBody.seed ? '-seed' : '');
    setRunning(p => ({ ...p, [key]: true }));
    setRunMsg(p => ({ ...p, [key]: '' }));
    try {
      const body = { ...extraBody };
      if (selectedChannel[agentType]) body.channel_code = selectedChannel[agentType];
      const r = await fetch(`${API}/agents/${agentType}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      setRunMsg(p => ({ ...p, [key]: d.message || `Run ID: ${d.run_id}` }));
      setTimeout(load, 2000);
    } catch (e) {
      setRunMsg(p => ({ ...p, [key]: 'Error: ' + e.message }));
    }
    setRunning(p => ({ ...p, [key]: false }));
  };

  const handlePipeline = async () => {
    setPipelineRunning(true);
    setPipelinePhase('starting');
    setPipelineMsg('');
    try {
      const r = await fetch(`${API}/agents/pipeline/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (d.success) {
        setPipelinePhase('running');
        setPipelineMsg(d.message);
        // Poll until both runs are done
        const scoutId  = d.scout_run_id;
        const analystId = d.analyst_run_id;
        const poll = setInterval(async () => {
          try {
            const rows = await fetch(`${API}/agents`).then(r2 => r2.json());
            const agentMap = {};
            (rows.agents || []).forEach(a => { agentMap[a.type] = a.last_run; });
            const scoutDone  = agentMap.scout?.id  >= scoutId  && ['completed','failed'].includes(agentMap.scout?.status);
            const analystDone = agentMap.analyst?.id >= analystId && ['completed','failed'].includes(agentMap.analyst?.status);
            if (scoutDone && !analystDone) setPipelinePhase('analyst');
            if (analystDone) {
              clearInterval(poll);
              setPipelinePhase('done');
              setPipelineRunning(false);
              setPipelineMsg('Pipeline complete. All program pages scraped and channels scored. Refresh the Government tab to see updated overviews and fit scores.');
              load();
            }
          } catch {}
        }, 5000);
      } else {
        setPipelineMsg('Error: ' + (d.error || 'Unknown error'));
        setPipelineRunning(false);
        setPipelinePhase('');
      }
    } catch (e) {
      setPipelineMsg('Error: ' + e.message);
      setPipelineRunning(false);
      setPipelinePhase('');
    }
  };

  if (!agentsData) return <div className="fr-loading">Loading agents…</div>;

  const agentMap = {};
  (agentsData.agents || []).forEach(a => { agentMap[a.type] = a; });

  const phaseLabel = { starting: 'Starting…', running: 'Scout fetching pages…', analyst: 'Analyst scoring channels…', done: 'Complete' };

  return (
    <div>
      {/* ── Full Pipeline Banner ─────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,.15) 0%, rgba(16,185,129,.10) 100%)',
        border: '1px solid rgba(99,102,241,.35)',
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text, #e8eaf6)', marginBottom: 4 }}>
              🚀 Run Full Pipeline
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted, #6b7280)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text, #e8eaf6)' }}>Step 1 — Scout (seed):</strong> fetches every program's official website, extracts readable content, writes it to the program's Overview file on disk.
              <br />
              <strong style={{ color: 'var(--text, #e8eaf6)' }}>Step 2 — Analyst:</strong> reads each Overview + requirements, scores every channel 0–100 against OptiPrime's profile, updates the fit scores and rationale you see on the Government cards.
              <br />
              <span style={{ opacity: 0.7 }}>Runs sequentially in the background. Takes ~3–8 min for all 26 channels depending on network.</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handlePipeline}
            disabled={pipelineRunning}
            style={{
              background: pipelineRunning ? 'rgba(99,102,241,.3)' : 'linear-gradient(135deg, #6366f1, #10b981)',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              cursor: pipelineRunning ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              minWidth: 180,
              opacity: pipelineRunning ? 0.75 : 1,
            }}
          >
            {pipelineRunning ? `${phaseLabel[pipelinePhase] || 'Running…'}` : '▶ Scrape + Score All'}
          </button>
        </div>

        {/* Progress bar */}
        {pipelineRunning && (
          <div style={{ marginTop: 12 }}>
            <div style={{ height: 4, background: 'rgba(255,255,255,.08)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                borderRadius: 2,
                background: 'linear-gradient(90deg, #6366f1, #10b981)',
                width: pipelinePhase === 'analyst' ? '65%' : pipelinePhase === 'done' ? '100%' : '15%',
                transition: 'width 1s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--muted, #6b7280)' }}>
              <span style={{ color: ['running','analyst','done'].includes(pipelinePhase) ? '#10b981' : '#6b7280' }}>✓ Scout (seed)</span>
              <span style={{ color: ['analyst','done'].includes(pipelinePhase) ? '#10b981' : '#6b7280' }}>✓ Analyst (score)</span>
              <span style={{ color: pipelinePhase === 'done' ? '#10b981' : '#6b7280' }}>✓ Done</span>
            </div>
          </div>
        )}
        {pipelineMsg && (
          <div style={{ marginTop: 10, fontSize: 12, color: pipelinePhase === 'done' ? '#10b981' : 'var(--accent, #19efff)', lineHeight: 1.5 }}>
            {pipelineMsg}
          </div>
        )}
      </div>

      {/* ── Per-agent cards ───────────────────────────────────────────────── */}
      <div className="fr-agent-cards">
        {Object.entries(AGENT_INFO).map(([type, info]) => {
          const agent = agentMap[type] || {};
          const lastRun = agent.last_run || {};
          const statusColor = lastRun.status === 'completed' ? '#10b981' : lastRun.status === 'running' ? '#3b82f6' : lastRun.status === 'failed' ? '#ef4444' : '#6b7280';

          return (
            <div key={type} className="fr-agent-card">
              <div className="fr-agent-card-header">
                <div className="fr-agent-card-name">{info.icon} {type}</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  {lastRun.trigger_mode && <ModePill mode={lastRun.trigger_mode} />}
                  {lastRun.status && (
                    <span className={`fr-badge fr-badge-${lastRun.status === 'completed' ? 'green' : lastRun.status === 'running' ? 'blue' : lastRun.status === 'failed' ? 'red' : 'info'}`}>
                      {lastRun.status}
                    </span>
                  )}
                </div>
              </div>

              <div className="fr-agent-meta">
                <span>{info.purpose}</span>
                <span>Cadence: {info.cadence}</span>
                {lastRun.started_at && <span>Last run: {fmtDate(lastRun.started_at)}</span>}
                {lastRun.started_at && <span>Duration: {fmtDuration(lastRun.started_at, lastRun.finished_at)}</span>}
                {lastRun.confidence != null && <span>Confidence: {Math.round(lastRun.confidence)}%</span>}
              </div>

              {agent.cycle_log_snippet && (
                <div className="fr-agent-log">{agent.cycle_log_snippet || 'No log entries.'}</div>
              )}
              {lastRun.summary_md && (
                <div className="fr-agent-log">{lastRun.summary_md.slice(0, 300)}</div>
              )}

              {/* Scout gets two buttons */}
              {type === 'scout' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <select
                    className="fr-select"
                    style={{ width: '100%', fontSize: 12 }}
                    value={selectedChannel[type] || ''}
                    onChange={e => setSelectedChannel(p => ({ ...p, [type]: e.target.value }))}
                  >
                    <option value="">All channels</option>
                    {channels.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <button
                    type="button"
                    className="fr-btn"
                    style={{ width: '100%', fontSize: 12, background: 'linear-gradient(135deg, #6366f1, #10b981)' }}
                    onClick={() => handleRun('scout', { seed: true })}
                    disabled={running['scout-seed']}
                    title="Fetch official program pages and write Overview files to disk"
                  >
                    {running['scout-seed'] ? 'Seeding…' : '🌐 Seed Overviews from Official Sites'}
                  </button>
                  <button
                    type="button"
                    className="fr-btn-outline"
                    style={{ width: '100%', fontSize: 12 }}
                    onClick={() => handleRun('scout')}
                    disabled={running['scout']}
                    title="Monitor pages for changes only — does not rewrite Overviews"
                  >
                    {running['scout'] ? 'Running…' : '🔄 Monitor Changes Only'}
                  </button>
                  {runMsg['scout-seed'] && <div style={{ fontSize: 11, color: 'var(--accent, #19efff)', marginTop: 4, lineHeight: 1.5 }}>{runMsg['scout-seed']}</div>}
                  {runMsg['scout']      && <div style={{ fontSize: 11, color: 'var(--muted, #6b7280)', marginTop: 4 }}>{runMsg['scout']}</div>}
                </div>
              )}

              {/* Analyst */}
              {type === 'analyst' && (
                <div>
                  <select
                    className="fr-select"
                    style={{ width: '100%', marginBottom: 8, fontSize: 12 }}
                    value={selectedChannel[type] || ''}
                    onChange={e => setSelectedChannel(p => ({ ...p, [type]: e.target.value }))}
                  >
                    <option value="">All channels</option>
                    {channels.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <button
                    type="button"
                    className="fr-btn"
                    style={{ width: '100%', fontSize: 12 }}
                    onClick={() => handleRun('analyst')}
                    disabled={running['analyst']}
                  >
                    {running['analyst'] ? 'Running…' : '▶ Run analyst'}
                  </button>
                  {runMsg['analyst'] && <div style={{ fontSize: 11, color: 'var(--accent, #19efff)', marginTop: 6 }}>{runMsg['analyst']}</div>}
                </div>
              )}

              {/* Builder, Compliance, Coordinator */}
              {['builder', 'compliance', 'coordinator'].includes(type) && (
                <div>
                  <button
                    type="button"
                    className="fr-btn"
                    style={{ width: '100%', fontSize: 12 }}
                    onClick={() => handleRun(type)}
                    disabled={running[type]}
                  >
                    {running[type] ? 'Running…' : `▶ Run ${type}`}
                  </button>
                  {runMsg[type] && <div style={{ fontSize: 11, color: 'var(--accent, #19efff)', marginTop: 6 }}>{runMsg[type]}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Help note ─────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--card-bg2, #1a1d2e)', border: '1px solid var(--card-border, #2a2d3e)', borderRadius: 8, fontSize: 12, color: 'var(--muted, #6b7280)', lineHeight: 1.6 }}>
        <strong style={{ color: 'var(--text, #e8eaf6)' }}>What each mode does:</strong><br />
        🚀 <strong style={{ color: 'var(--text, #e8eaf6)' }}>Scrape + Score All</strong> — full pipeline: fetches every official government page → writes Overview files → scores all channels. Run this first.<br />
        🌐 <strong style={{ color: 'var(--text, #e8eaf6)' }}>Seed Overviews</strong> — Scout only: fills in program Overview files from official sites (no scoring).<br />
        🔄 <strong style={{ color: 'var(--text, #e8eaf6)' }}>Monitor Changes</strong> — Scout only: checks for changes vs last visit, creates alerts if something changed.<br />
        📊 <strong style={{ color: 'var(--text, #e8eaf6)' }}>Run analyst</strong> — re-scores all channels using whatever Overviews are already on disk.
      </div>
    </div>
  );
}
